import { ComponentPropsWithoutRef, MouseEvent, Ref, forwardRef } from "react";
import { useTrackingId } from "../../hooks";
import {
  DataTrackingId,
  LayoutUtilProps,
  OnCloseProps,
  Status,
  Svg,
} from "../../types";
import Close from "@servicetitan/hammer-icon/mdi/round/close.svg";
import Info from "@servicetitan/hammer-icon/mdi/round/info.svg";
import Warning from "@servicetitan/hammer-icon/mdi/round/warning.svg";
import Danger from "@servicetitan/hammer-icon/mdi/round/error.svg";
import Success from "@servicetitan/hammer-icon/mdi/round/check_circle.svg";
import { useLayoutPropsUtil } from "../../internal/hooks";
import Button from "../Button";
import Icon from "../Icon";

import cx from "classnames";
import styles from "./Announcement.module.scss";

/**
 * Props for the Announcement component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 * @extends OnCloseProps
 */
export type AnnouncementProps = ComponentPropsWithoutRef<"div"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> &
  OnCloseProps & {
    /**
     * The visual style of the announcement.
     * @default info
     */
    status?: Status;
    /**
     * The title text displayed in the announcement.
     */
    title: string;
  } &
  /**
   * Data tracking id
   */
  DataTrackingId;

const statusIconMap: Record<NonNullable<AnnouncementProps["status"]>, Svg> = {
  info: Info,
  success: Success,
  warning: Warning,
  danger: Danger,
};

/**
 * Announcement component for displaying important messages to users.
 *
 * Features:
 * - Supports different visual styles (info, success, warning, danger)
 * - Optional close button for dismissible announcements
 * - Customizable title and content
 * - Accessible with proper ARIA roles
 * - Includes status-specific icons
 * - Supports layout utilities for positioning and spacing
 * - Includes data tracking capabilities
 *
 * @example
 * <Announcement
 *   title="New Feature"
 *   onClose={() => console.log('Announcement closed')}
 * >
 *   Check out our latest feature!
 * </Announcement>
 *
 * @example
 * <Announcement
 *   status="warning"
 *   title="Maintenance Notice"
 * >
 *   Scheduled maintenance will begin in 30 minutes.
 * </Announcement>
 */
export const Announcement = forwardRef(
  (props: AnnouncementProps, ref: Ref<HTMLDivElement>) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

    const {
      className,
      title,
      status = "info",
      children,
      style,
      onClose,
      ...rest
    } = componentProps;

    const data = {
      title,
      status,
    };

    const trackingId = useTrackingId({
      name: "Announcement",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const AnnouncementCx = cx(styles["announcement"], className, {
      [styles["success"]]: status === "success",
      [styles["warning"]]: status === "warning",
      [styles["danger"]]: status === "danger",
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <div
        data-tracking-id={trackingId}
        role="status"
        className={AnnouncementCx}
        data-anv="announcement"
        style={styleCombined}
        {...rest}
        ref={ref}
      >
        <span className={styles["title-container"]}>
          <Icon
            size="large"
            className={styles["status-icon"]}
            svg={statusIconMap[status]}
          />

          <span className={styles["title"]}>{title}</span>
        </span>

        {children}

        {onClose ? (
          <span className={styles["close-container"]}>
            <Button
              icon={Close}
              size="small"
              appearance="ghost"
              className={styles["close-button"]}
              onClick={(e: MouseEvent<HTMLButtonElement>) => onClose(e)}
              aria-label={`Dismiss ${status} announcement`}
            />
          </span>
        ) : null}
      </div>
    );
  },
);

Announcement.displayName = "Announcement";
